import './RedeemCoupon.pcss';
import React, { useCallback, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import classnames from 'classnames';
import { StoreState } from '@mm/state/store-interfaces';
import { CouponObject } from '@mm/state/controllers/SubscriptionsController';
import CompanyModel from '@mm/state/model/CompanyModel';
import { ArrowStatefulIcon } from '@mm/icons';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import TextInput from '../../../../../../../common/components/TextInput';
import FormFieldWrapper from '../../../../../../../common/components/FormFieldWrapper/FormFieldWrapper';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import ErrorMessage from '../../../../../../../common/components/ErrorMessage/ErrorMessage';

type OuterProps = {
  company: CompanyModel;
  onCouponRedeem: (couponData?: CouponObject) => any;
  planCode: string;
  className?: string;
};

type InnerProps = ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const errorMessage = `
  Sorry, that coupon code is invalid. Please contact support@mobilemonkey.com if you think this is a mistake.
`;

const RedeemCoupon = (props: Props) => {
  const { retrieveCoupon, onCouponRedeem, company, planCode, className } =
    props;
  const [showRedeemCoupon, setShowRedeemCoupon] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponInvalid, setCouponInvalid] = useState(false);
  const [coupon, setCoupon] = useState<CouponObject | undefined>(undefined);

  const couponDiscountSuffix = useMemo(() => {
    if (!coupon || !coupon.discount) return '';
    switch (coupon.discount.type) {
      case 'percent':
        return '%';
      case 'dollars':
        return '$';
      default:
    }
    return '';
  }, [coupon]);

  const redeemCoupon = useCallback(async () => {
    setCouponInvalid(false);
    const couponData = await retrieveCoupon(couponInput, planCode, company.id);

    if (couponData && !!couponData.discount) {
      setCoupon(couponData);
      onCouponRedeem(couponData);
    } else {
      setCouponInvalid(true);
    }
  }, [
    couponInput,
    onCouponRedeem,
    retrieveCoupon,
    company,
    planCode,
    setCouponInvalid,
  ]);

  const removeCoupon = () => {
    setCoupon(undefined);
    onCouponRedeem(undefined);
    setCouponInput('');
    setShowRedeemCoupon(false);
  };

  return (
    <div className={classnames('redeem-coupon', className)}>
      <div
        className={classnames('redeem-coupon__add-coupon-button', className)}
        onClick={() => setShowRedeemCoupon(!showRedeemCoupon)}
      >
        <ArrowStatefulIcon
          color="#2F54EB"
          direction={showRedeemCoupon ? 'up' : 'down'}
          size={10}
          className="redeem-coupon__arrow-icon"
        />
        I have a coupon
      </div>
      {showRedeemCoupon && (
        <FormFieldWrapper className="redeem-coupon__form-field-wrapper">
          {!coupon && (
            <>
              <TextInput
                onChange={event => setCouponInput(event.target.value)}
                value={couponInput}
              />

              <PrimaryButton
                className="redeem-coupon__button"
                onClick={redeemCoupon}
              >
                Redeem
              </PrimaryButton>
            </>
          )}
          {coupon && (
            <div className="redeem-coupon__applied-message">
              Coupon &quot;{coupon.code}&quot; applied
              {coupon.discount && (
                <span>
                  : -{coupon.discount.value}
                  {couponDiscountSuffix}
                </span>
              )}
              <span
                className="redeem-coupon__remove-button"
                onClick={removeCoupon}
              >
                Remove
              </span>
            </div>
          )}
        </FormFieldWrapper>
      )}

      {couponInvalid && <ErrorMessage error={errorMessage} />}
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  retrieveCoupon: (coupon: string, planCode: string, companyId: number) =>
    dispatch(
      subscriptionCRUDActions.getCouponData(coupon, planCode, companyId),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(null, mapDispatchToProps),
);

export default enhance(RedeemCoupon);
